import { useContext, useEffect, useState } from 'react';
import Highcharts, {
  Options as HighChartOptions,
  SeriesGaugeOptions,
} from 'highcharts/highcharts';

import AccountContext from '../../../contexts/AccountContext';
import EntityDetailsContext from '../../../screens/EntityDetailsShow/EntityDetailsContext';
import useValueFormatters from '../../../hooks/useValueFormatters';
import usePeerComparison from './usePeerComparison';

const useGaugeData = ({
  gauge,
}: {
  gauge: VisualisationDefinitions.Gauge;
}): {
  chartOptions: HighChartOptions | undefined;
  current: string;
} => {
  const { unitsLocale } = useContext(AccountContext);
  const { data, isLoading, formatting, isLowerBetter } = usePeerComparison({
    gauge,
  });
  const [chartOptions, setChartOptions] = useState<
    HighChartOptions | undefined
  >();
  const entityDetails = useContext(EntityDetailsContext);
  const [current, setCurrent] = useState<string>('');
  const { formatMetric } = useValueFormatters();

  useEffect(() => {
    if (isLoading || !data) {
      setChartOptions(undefined);
      return;
    }

    const MINIMUM_FILL_PERCENT = 10;

    // The server normalises percentile so 100 = best regardless of lowerIsBetter.
    // Fill directly from the left (0%) to right (100%) — no axis reversal needed.
    const displayValue = Math.max(data.percentile, MINIMUM_FILL_PERCENT);
    const formattedValue = formatMetric({
      value: data.value,
      metricId: gauge.metricId,
    });
    setCurrent(formattedValue);

    const series = [
      {
        name: gauge.entity,
        data: [displayValue],
        dataLabels: {
          format: `<div style="text-align:center"><span style="font-size:25px;color:var(--color-text-primary)">${formattedValue}</span></div>`,
        },
      },
    ] as SeriesGaugeOptions[];
    setChartOptions({
      accessibility: {
        enabled: false,
      },
      chart: {
        type: 'solidgauge',
      },
      title: {
        text: undefined,
      },
      pane: {
        center: ['50%', '60%'],
        reverse: true,
        startAngle: -90,
        endAngle: 90,
        background: {
          // @ts-ignore
          backgroundColor:
            // @ts-ignore
            Highcharts.defaultOptions.legend.backgroundColor ||
            'var(--color-bg-tertiary)',
          innerRadius: '60%',
          outerRadius: '100%',
          shape: 'arc',
        },
      },
      plotOptions: {
        solidgauge: {
          dataLabels: {
            y: 30,
            borderWidth: 0,
            useHTML: true,
            crop: false,
            overflow: 'allow',
            style: { fontSize: `20px` },
            allowOverlap: true,
            formatter: () => {
              return current;
            },
            nullFormatter: () => current,
          },
        },
      },
      tooltip: {
        style: {
          color: 'var(--color-text-primary)',
        },
        formatter: () => {
          let tooltipText = '';
          if (gauge.entity !== 'Dynamic') {
            tooltipText = `${gauge.entity}: ${formattedValue}`;
          } else if (entityDetails?.selectedEntityLabel) {
            tooltipText = `${entityDetails?.selectedEntityLabel}: ${formattedValue}`;
          } else {
            tooltipText = formattedValue;
          }
          return tooltipText;
        },
      },
      yAxis: {
        // Percentile is server-normalised (100 = best), so danger→success always
        // maps correctly from left (low percentile) to right (high percentile).
        stops: [
          [0.33, 'var(--color-status-danger)'],
          [0.66, 'var(--color-status-warning)'],
          [1, 'var(--color-status-success)'],
        ],
        lineWidth: 1,
        tickWidth: 1,
        tickAmount: 2,
        tickPositions: [0, 100],
        title: {
          text: '',
        },
        labels: {
          style: {
            fontSize: `11px`,
          },
          y: 11,
          formatter: (x) => {
            // Where lower is better, percentile 0 = worst = highest absolute
            // value. Otherwise percentile 0 = worst = lowest absolute value.
            const metricValue = isLowerBetter
              ? x.value === 0
                ? data.max
                : data.min
              : x.value === 0
                ? data.min
                : data.max;
            return formatMetric({
              value: metricValue,
              metricId: gauge.metricId,
            });
          },
        },
        min: 0,
        max: 100,
      },
      credits: {
        enabled: false,
      },
      series,
    });
  }, [
    current,
    data,
    entityDetails?.selectedEntityLabel,
    formatMetric,
    formatting,
    gauge.entity,
    gauge.metricId,
    gauge.name,
    isLoading,
    isLowerBetter,
    unitsLocale,
  ]);

  return { chartOptions, current };
};

export default useGaugeData;
